import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Lock, CheckCircle } from "lucide-react";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  head: () => ({
    meta: [
      { title: "Reset Your Password — Inquira" },
      {
        name: "description",
        content:
          "Choose a new password for your Inquira account and get straight back to solving problems with step-by-step AI explanations.",
      },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Reset Your Password — Inquira" },
      {
        property: "og:description",
        content: "Set a new password for your Inquira account and get back to step-by-step AI study help.",
      },
      { property: "og:url", content: "https://www.inquira.online/reset-password" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Reset Your Password — Inquira" },
      {
        name: "twitter:description",
        content: "Set a new password for your Inquira account and get back to step-by-step AI study help.",
      },
    ],
    links: [{ rel: "canonical", href: "https://www.inquira.online/reset-password" }],
  }),
});

function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isRecovery, setIsRecovery] = useState(false);

  useEffect(() => {
    // Check for recovery token in URL hash
    const hash = window.location.hash;
    if (hash.includes("type=recovery")) {
      setIsRecovery(true);
    }

    // Listen for auth state change with recovery event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        setIsRecovery(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);

    if (error) {
      setError(error.message);
    } else {
      setSuccess(true);
    }
  };

  if (!isRecovery && !success) {
    return (
      <div className="min-h-screen notebook-lines flex items-center justify-center px-4">
        <div className="w-full max-w-sm space-y-6 text-center">
          <p className="text-ink-muted">Invalid or expired reset link.</p>
          <Link to="/login" className="text-neon-pink font-semibold text-sm">Back to Sign In</Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen notebook-lines flex items-center justify-center px-4">
        <div className="w-full max-w-sm space-y-6 text-center">
          <div className="size-16 mx-auto rounded-full bg-neon-pink/10 flex items-center justify-center">
            <CheckCircle className="size-8 text-neon-pink" />
          </div>
          <h1 className="font-[family-name:var(--font-display)] font-extrabold text-2xl text-ink">
            Password Updated!
          </h1>
          <p className="text-sm text-ink-muted">Your password has been reset successfully.</p>
          <Link to="/">
            <Button variant="scan" size="full" className="font-[family-name:var(--font-display)]">
              Start Solving
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen notebook-lines flex items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-6">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink transition-colors">
          <ArrowLeft className="size-4" /> Back to Sign In
        </Link>

        <div className="text-center space-y-2">
          <h1 className="font-[family-name:var(--font-display)] font-extrabold text-2xl text-ink">
            Set New Password
          </h1>
          <p className="text-sm text-ink-muted">Choose a new password for your account</p>
        </div>

        <div className="bg-card rounded-2xl p-6 border border-rule shadow-sm space-y-4">
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm rounded-xl p-3 border border-destructive/20">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-ink-muted">New Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-ink-muted" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-paper border border-rule rounded-xl py-3 pl-10 pr-4 text-sm text-ink placeholder:text-ink-muted/50 focus:outline-none focus:ring-2 focus:ring-neon-blue/30"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-widest text-ink-muted">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-ink-muted" />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  className="w-full bg-paper border border-rule rounded-xl py-3 pl-10 pr-4 text-sm text-ink placeholder:text-ink-muted/50 focus:outline-none focus:ring-2 focus:ring-neon-blue/30"
                  placeholder="••••••••"
                />
              </div>
            </div>

            <Button
              type="submit"
              variant="scan"
              size="full"
              disabled={loading}
              className="font-[family-name:var(--font-display)]"
            >
              {loading ? "Updating..." : "Update Password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
